import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import {
  dialogContentStyle,
  dialogStyle,
  openedServiceAgreementGroupStyle,
  serviceAgreementGroupStyle,
} from './dialogStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ProductDocumentsToDistributeDialogAgreementSection from './ProductDocumentsToDistributeDialogAgreementSection';
import ProductDocumentToDistributeSearch from './ProductDocumentToDistributeSearch';
import { jbColors } from 'theme/jbColors';
import { sendBulkDocuments } from 'thunks/productDocumentsThunk';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import ClearIcon from '@mui/icons-material/Clear';

export interface ItemForDistribution {
  taskId: string;
  documentUid: string;
  language: string;
}

interface DocumentItem {
  documentUid: string;
  documentLanguages?: string[];
  [key: string]: unknown;
}

export interface DistributeItem {
  serviceName: string;
  serviceAgreementId: string | null;
  serviceRoleId?: string | null;
  taskId: string;
  status?: string;
  documents: DocumentItem[];
  [key: string]: unknown;
}

type ProductDocumentsToDistributeDialogProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  itemsToDistribute: DistributeItem[];
  brUid?: string;
  partnerId?: string;
};

const ProductDocumentsToDistributeDialog = ({
  isOpen,
  setIsOpen,
  itemsToDistribute,
  brUid,
  partnerId,
}: ProductDocumentsToDistributeDialogProps) => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [checkedDocuments, setCheckedDocuments] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();
  const [selectedLanguageForService, setSelectedLanguageForService] = useState<{ key: string; value: string }[]>([]);
  const [languangeMap, setLanguangeMap] = useState<{
    languagesPerGroupedAgreement: { serviceName: string; value: string }[];
    languagesPerAgreement: { agreementId: string; value: string }[];
    languagesPerDocument: { documentUid: string; value: string }[];
  }>({
    languagesPerGroupedAgreement: [],
    languagesPerAgreement: [],
    languagesPerDocument: [],
  });
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  const localItemsToDistribute = useMemo<DistributeItem[]>(() => {
    if (!itemsToDistribute) return [];
    if (search && search !== '') {
      return itemsToDistribute.filter((item: DistributeItem) =>
        item.serviceName?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return itemsToDistribute;
  }, [search, itemsToDistribute]);

  const itemsSelectedForDistribution = useMemo<ItemForDistribution[]>(() => {
    if (checkedDocuments.length === 0) return [];
    const temp: ItemForDistribution[] = [];
    checkedDocuments.forEach((item) => {
      const agreement = localItemsToDistribute.find((agreement) =>
        agreement.documents.some((doc) => doc.documentUid === item),
      );
      const document = agreement?.documents.find((doc) => doc.documentUid === item);
      const language =
        languangeMap?.languagesPerDocument?.filter((l) => l.documentUid === item)[0]?.value ??
        languangeMap?.languagesPerAgreement?.filter((l) => l.agreementId === agreement?.serviceAgreementId)[0]?.value ??
        languangeMap?.languagesPerGroupedAgreement?.filter((l) => l.serviceName === agreement?.serviceName)[0]?.value ??
        'EN';

      if (agreement && document) {
        temp.push({
          taskId: agreement.taskId,
          documentUid: document.documentUid,
          language: language,
        });
      }
    });
    return temp;
  }, [localItemsToDistribute, checkedDocuments, languangeMap]);

  const groupedItems =
    localItemsToDistribute && localItemsToDistribute.length > 0
      ? localItemsToDistribute?.reduce((groups: Record<string, DistributeItem[]>, item) => {
          const { serviceName } = item;
          if (!groups[serviceName]) {
            groups[serviceName] = [];
          }
          groups[serviceName].push(item);
          return groups;
        }, {})
      : {};

  const handleLanguageChange = (e: { target: { value: string } } | null, serviceName: string) => {
    if (!e) {
      setSelectedLanguageForService((prev) => prev.filter((l) => l.key !== serviceName));
      setLanguangeMap((prev) => ({
        ...prev,
        languagesPerGroupedAgreement: [
          ...(prev?.languagesPerGroupedAgreement.filter((l) => l.serviceName !== serviceName) ?? []),
        ],
      }));
      return;
    }

    setSelectedLanguageForService((prev) => [
      ...(prev?.filter((d) => d.key !== serviceName) ?? []),
      { key: serviceName, value: e.target.value },
    ]);
    if (serviceName && e.target.value) {
      setLanguangeMap((prev) => ({
        ...prev,
        languagesPerGroupedAgreement: [
          ...(prev?.languagesPerGroupedAgreement ?? []),
          { serviceName: serviceName, value: e.target.value },
        ],
      }));
    }
  };

  const handleSend = () => {
    console.log(
      'Sending items......',
      itemsSelectedForDistribution,
      `${brUid ? 'BR' : 'PARTNER:'}${brUid ?? partnerId}`,
      '...........#####',
    );
    dispatch(sendBulkDocuments(itemsSelectedForDistribution, brUid ?? partnerId ?? '', CLM_URL));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>) => {
    if ((e.target as HTMLInputElement)?.checked) {
      setCheckedDocuments(localItemsToDistribute.map((item) => item.documents.map((doc) => doc.documentUid)).flat());
    } else {
      setCheckedDocuments([]);
    }
  };

  function getCommonLanguages(items: DistributeItem[]) {
    const documents = items.flatMap((i: DistributeItem) => i.documents);

    return documents
      .flatMap((d: DocumentItem) => d.documentLanguages as string[])
      .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
  }

  const handleSelectedAgreementGroups = (
    e: React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<HTMLInputElement>,
    items: DistributeItem[],
  ) => {
    const documentIds = items.flatMap((i: DistributeItem) => i.documents).map((d: DocumentItem) => d.documentUid);

    if (!checkedDocuments.some((a) => documentIds.includes(a))) {
      const temp = localItemsToDistribute.filter((i) => i.serviceName === items[0].serviceName);
      const documents = temp.flatMap((i) => i.documents).map((d) => d.documentUid) ?? [];
      const merged = [...checkedDocuments, ...documents];

      setCheckedDocuments(merged);
    } else {
      const linkedDocs = items.flatMap((i: DistributeItem) => i.documents).map((d: DocumentItem) => d.documentUid);
      const temp = checkedDocuments.filter((c) => !linkedDocs.includes(c));
      setCheckedDocuments(temp);
    }
  };

  const handleSelectedAgreements = (e: DistributeItem) => {
    const documents = e.documents.map((doc: DocumentItem) => doc.documentUid);
    const documentIds = e.documents.map((doc: DocumentItem) => doc.documentUid);

    if (!checkedDocuments?.some((agreement) => documentIds.includes(agreement))) {
      const newDocuments = [...checkedDocuments, ...e.documents.map((doc: DocumentItem) => doc.documentUid)];
      setCheckedDocuments(newDocuments);
    } else {
      const checked = checkedDocuments.filter((checkedDocument) => !documents.includes(checkedDocument));
      setCheckedDocuments(checked);
    }
  };

  const toggleItem = (e: React.MouseEvent<HTMLButtonElement>, serviceName: string) => {
    e.stopPropagation();
    e.preventDefault();

    setOpenItems((prevOpenItems) => ({
      ...prevOpenItems,
      [serviceName]: !prevOpenItems[serviceName],
    }));
  };

  const handleSelectedDocuments = (e: DocumentItem) => {
    if (!checkedDocuments.includes(e.documentUid)) {
      setCheckedDocuments([...checkedDocuments, e.documentUid]);
    } else {
      setCheckedDocuments(checkedDocuments.filter((checkedDocument) => checkedDocument !== e.documentUid));
    }
  };

  return (
    <Dialog
      open={isOpen}
      sx={dialogStyle}
      title={''}
      onClose={() => {
        setCheckedDocuments([]);
        setOpenItems({});
        setIsOpen(false);
      }}
    >
      <DialogContent sx={dialogContentStyle}>
        <Grid container>
          <Grid
            item
            xs={1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyItems: 'flex-start',
              width: '16rem',
            }}
          >
            <Checkbox data-testid={'select-all-checkbox'} onClick={handleSelectAll} />
            <Typography variant="body1">All</Typography>
          </Grid>
          <Grid item xs={1.5}></Grid>
          <Grid item xs={9.5}>
            <h2>Documents pending distribution</h2>
          </Grid>
          <Divider sx={{ height: '2px', width: '100%', mt: 0.5, mb: 0.2 }} />
        </Grid>
        <Grid container>
          <ProductDocumentToDistributeSearch setSearch={setSearch} />
        </Grid>
        <Grid container sx={[dialogContentStyle, { minHeight: '25em', overflow: 'auto', p: 1 }]}>
          {Object.entries(groupedItems).map(([serviceName, items]: [string, DistributeItem[]]) => (
            <Grid
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                height: 'fit-content',
                mt: 1,
              }}
              container
              key={serviceName}
            >
              <Grid container sx={serviceAgreementGroupStyle(openItems[serviceName])}>
                <Grid item xs={1}>
                  <Checkbox
                    data-testid="document-group-checkbox"
                    checked={checkedDocuments.some((a) =>
                      items
                        .flatMap((i) => i.documents)
                        .map((d) => d.documentUid)
                        .includes(a),
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectedAgreementGroups(e, items);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h5" sx={{ mb: 0 }}>
                    {serviceName}:{' '}
                    {Array.isArray(items.flatMap((i) => i.documents)) && items?.flatMap((i) => i.documents)?.length}
                  </Typography>
                </Grid>
                <Grid item xs={5} sx={{ mt: '0.5em', display: 'flex' }}>
                  <Grid container item xs={10} sx={{ display: 'grid', width: '100%' }}>
                    {!openItems[serviceName] && (
                      <FormControl size="small">
                        <InputLabel id="demo-simple-select-filled-label">Language</InputLabel>
                        <Select
                          endAdornment={
                            selectedLanguageForService?.filter((l) => l.key === serviceName)[0] && (
                              <ClearIcon
                                sx={{
                                  height: '0.5em',
                                  mr: 1,
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleLanguageChange(null, serviceName)}
                              />
                            )
                          }
                          onChange={(e) => handleLanguageChange(e, serviceName)}
                          value={selectedLanguageForService?.filter((l) => l.key === serviceName)[0]?.value ?? ''}
                          id="demo-simple-select-filled"
                          label="Language"
                        >
                          {getCommonLanguages(items).map((l: string) => (
                            <MenuItem key={l} value={l}>
                              {l}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  <Grid container item xs={2} sx={{ display: 'grid', width: '100%' }}>
                    <IconButton
                      onClick={(e) => toggleItem(e, serviceName)}
                      aria-label={openItems[serviceName] ? 'Collapse' : 'Expand'}
                    >
                      {openItems[serviceName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              {openItems[serviceName] && (
                <Grid container sx={openedServiceAgreementGroupStyle(openItems[serviceName])}>
                  <ProductDocumentsToDistributeDialogAgreementSection
                    setLanguangeMap={setLanguangeMap}
                    serviceAgreements={items}
                    checkedAgreements={checkedDocuments}
                    handleSelectItem={handleSelectedAgreements}
                    handleSelectDocument={handleSelectedDocuments}
                    toggleItem={toggleItem}
                    checkedDocuments={checkedDocuments}
                    openItems={openItems}
                  />
                </Grid>
              )}
            </Grid>
          ))}
        </Grid>
        <DialogActions>
          <Button
            sx={{
              backgroundColor: 'white',
              color: jbColors.core.reflexBlue[100],
              border: `1px solid ${jbColors.core.reflexBlue[100]}`,
              ':hover': {
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
              },
            }}
            onClick={() => {
              setCheckedDocuments([]);
              setOpenItems({});
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          {checkedDocuments?.length === 0 ? (
            <Button
              disabled={checkedDocuments?.length === 0}
              onClick={() => {
                setCheckedDocuments([]);
                setOpenItems({});
                setIsOpen(false);
              }}
            >
              Distribute Documents
            </Button>
          ) : (
            <Button
              sx={{
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
                ':hover': {
                  backgroundColor: 'white',
                  color: jbColors.core.reflexBlue[100],
                },
              }}
              disabled={checkedDocuments?.length === 0}
              onClick={() => {
                handleSend();
                setCheckedDocuments([]);
                setOpenItems({});
                setIsOpen(false);
              }}
            >
              Distribute Documents
            </Button>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDocumentsToDistributeDialog;
